import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, LogIn, Building2, Wand2, ArrowLeft, CheckCircle, Shield, AlertTriangle, Ban, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import ReCAPTCHA from 'react-google-recaptcha';
import * as api from '../services/api';

// Get cached settings
const getCachedSettings = () => {
  try {
    const cached = localStorage.getItem('datacrm_settings_cache');
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {}
  return null;
};

export default function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMode, setLoginMode] = useState('password'); // 'password' or 'magic'
  const [codeSent, setCodeSent] = useState(false);
  const [magicCode, setMagicCode] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(0);
  const [passwordBlocked, setPasswordBlocked] = useState(false);
  const [accountBlocked, setAccountBlocked] = useState(false);
  const [passwordAttempts, setPasswordAttempts] = useState(0);
  const [magicLinkAttempts, setMagicLinkAttempts] = useState(0);
  const codeInputRefs = useRef([]);
  const recaptchaRef = useRef(null);
  
  const [settings, setSettings] = useState(() => getCachedSettings());
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await api.getSettings();
        setSettings(data);
        try {
          localStorage.setItem('datacrm_settings_cache', JSON.stringify(data));
        } catch (e) {}
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, []);

  // Countdown timer for code expiration
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const primaryColor = settings?.primaryColor || '#00b30c';
  const secondaryColor = settings?.secondaryColor || '#64f288';

  // Check login status when email changes
  useEffect(() => {
    const checkStatus = async () => {
      if (formData.email && formData.email.includes('@')) {
        try {
          const status = await api.getLoginStatus(formData.email);
          setPasswordBlocked(status.passwordBlocked);
          setAccountBlocked(status.accountBlocked);
          setPasswordAttempts(status.passwordAttempts);
          setMagicLinkAttempts(status.magicLinkAttempts);
          
          if (status.passwordBlocked && loginMode === 'password') {
            setLoginMode('magic');
          }
        } catch (e) {}
      }
    };
    
    const timer = setTimeout(checkStatus, 500);
    return () => clearTimeout(timer);
  }, [formData.email, loginMode]);

  // Check if reCAPTCHA is required - must have valid keys (minimum length check)
  const isRecaptchaEnabled = settings?.enableRecaptcha && 
    settings?.recaptchaSiteKey && 
    String(settings.recaptchaSiteKey).trim().length > 10;

  // Handle reCAPTCHA change
  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  // Handle reCAPTCHA expiration
  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
  };

  // Handle password login
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    
    // Validate reCAPTCHA if enabled
    if (isRecaptchaEnabled && !recaptchaToken) {
      toast.error('Por favor completa la verificación reCAPTCHA');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.login(formData.email, formData.password, recaptchaToken);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      onLogin(response.user);
      toast.success(`¡Bienvenido, ${response.user.name}!`);
      
      if (response.user.role === 'referidor') {
        navigate('/portal-referidos');
      } else if (response.user.role === 'trafficker') {
        navigate('/trafficker');
      } else {
        navigate('/');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Error de autenticación';
      toast.error(detail);
      
      setPasswordAttempts(prev => prev + 1);
      
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
      
      if (error?.response?.status === 429 || detail.includes('Demasiados intentos')) {
        setPasswordBlocked(true);
        setLoginMode('magic');
        toast.warning('Has excedido los intentos. Usa código de acceso.');
      }
      
      if (error?.response?.status === 403) {
        setAccountBlocked(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle magic code request
  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error('Por favor ingresa tu correo electrónico');
      return;
    }
    
    // Validate reCAPTCHA if enabled
    if (isRecaptchaEnabled && !recaptchaToken) {
      toast.error('Por favor completa la verificación reCAPTCHA');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.requestMagicLink(formData.email, recaptchaToken);
      setCodeSent(true);
      setCountdown(240); // 4 minutes
      setMagicCode(['', '', '', '', '', '']);
      toast.success('¡Código enviado a tu correo!');
      
      // Focus first input
      setTimeout(() => {
        if (codeInputRefs.current[0]) {
          codeInputRefs.current[0].focus();
        }
      }, 100);
    } catch (error) {
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
      setCodeSent(true);
      setCountdown(240);
      toast.success('¡Código enviado a tu correo!');
    } finally {
      setLoading(false);
    }
  };

  // Handle code input
  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...magicCode];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newCode[index + i] = digit;
        }
      });
      setMagicCode(newCode);
      
      // Focus appropriate input
      const nextIndex = Math.min(index + digits.length, 5);
      if (codeInputRefs.current[nextIndex]) {
        codeInputRefs.current[nextIndex].focus();
      }
    } else {
      const newCode = [...magicCode];
      newCode[index] = value.replace(/\D/g, '');
      setMagicCode(newCode);
      
      // Auto-focus next input
      if (value && index < 5 && codeInputRefs.current[index + 1]) {
        codeInputRefs.current[index + 1].focus();
      }
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !magicCode[index] && index > 0) {
      codeInputRefs.current[index - 1].focus();
    }
  };

  // Verify code
  const handleVerifyCode = async () => {
    const code = magicCode.join('');
    if (code.length !== 6) {
      toast.error('Ingresa el código completo de 6 dígitos');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.verifyMagicCode(formData.email, code);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      onLogin(response.user);
      toast.success(`¡Bienvenido, ${response.user.name}!`);
      
      if (response.user.role === 'referidor') {
        navigate('/portal-referidos');
      } else if (response.user.role === 'trafficker') {
        navigate('/trafficker');
      } else {
        navigate('/');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Código inválido';
      toast.error(detail);
      setMagicCode(['', '', '', '', '', '']);
      if (codeInputRefs.current[0]) {
        codeInputRefs.current[0].focus();
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-verify when code is complete
  useEffect(() => {
    const code = magicCode.join('');
    if (code.length === 6 && codeSent) {
      handleVerifyCode();
    }
  }, [magicCode]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Account blocked screen
  if (accountBlocked) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ background: `linear-gradient(135deg, #ef4444 0%, #f87171 100%)` }}
      >
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Ban className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Cuenta Bloqueada</h2>
            <p className="text-slate-500 mb-4">
              Tu cuenta ha sido bloqueada por seguridad debido a múltiples intentos fallidos.
            </p>
            <div className="bg-red-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-700">
                <strong>¿Qué hacer?</strong><br />
                Contacta al administrador del sistema para desbloquear tu cuenta.
              </p>
            </div>
            <button
              onClick={() => {
                setAccountBlocked(false);
                setPasswordBlocked(false);
                setPasswordAttempts(0);
                setMagicLinkAttempts(0);
                setFormData({ email: '', password: '' });
                setCodeSent(false);
              }}
              className="flex items-center justify-center gap-2 w-full py-3 text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Intentar con otro correo
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Code entry screen
  if (codeSent) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}
      >
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Ingresa el código</h2>
              <p className="text-slate-500 text-sm">
                Enviamos un código de 6 dígitos a<br />
                <strong>{formData.email}</strong>
              </p>
            </div>

            {/* Code inputs */}
            <div className="flex justify-center gap-2 mb-6">
              {magicCode.map((digit, index) => (
                <input
                  key={index}
                  ref={el => codeInputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-xl focus:border-green-500 focus:outline-none transition-colors"
                  style={{ '--tw-ring-color': primaryColor }}
                />
              ))}
            </div>

            {/* Timer */}
            {countdown > 0 ? (
              <p className="text-center text-sm text-slate-500 mb-4">
                El código expira en <span className="font-bold text-slate-700">{formatTime(countdown)}</span>
              </p>
            ) : (
              <p className="text-center text-sm text-red-500 mb-4">
                El código ha expirado
              </p>
            )}

            {/* Verify button */}
            <button
              onClick={handleVerifyCode}
              disabled={loading || magicCode.join('').length !== 6}
              className="w-full py-3.5 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2 mb-4"
              style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Verificar código
                </>
              )}
            </button>

            {/* Resend code */}
            <div className="text-center">
              <button
                onClick={() => {
                  if (countdown <= 0) {
                    handleRequestCode({ preventDefault: () => {} });
                  }
                }}
                disabled={countdown > 0}
                className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {countdown > 0 ? 'Espera para reenviar' : 'Reenviar código'}
              </button>
            </div>

            {/* Back button */}
            <button
              onClick={() => {
                setCodeSent(false);
                setMagicCode(['', '', '', '', '', '']);
                setCountdown(0);
              }}
              className="flex items-center justify-center gap-2 w-full py-3 text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors font-medium mt-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Cambiar correo
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          {settings?.logoUrl ? (
            <div className="inline-flex items-center justify-center mb-4">
              <img 
                src={settings.logoUrl} 
                alt={settings?.appName || 'Logo'} 
                className="h-16 w-auto object-contain"
              />
            </div>
          ) : (
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-4">
              <Building2 className="w-8 h-8" style={{ color: primaryColor }} />
            </div>
          )}
          <h1 className="text-3xl font-bold text-white">{settings?.appName || 'DataCRM'}</h1>
          <p className="text-white/80 mt-2">{settings?.appTagline || 'Gestión inteligente de clientes'}</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-slate-900">Iniciar Sesión</h2>
            <p className="text-sm text-slate-500 mt-1">
              {passwordBlocked 
                ? 'Usa código de acceso para ingresar'
                : loginMode === 'password' 
                  ? 'Ingresa tus credenciales'
                  : 'Recibe un código en tu correo'
              }
            </p>
          </div>

          {/* Password blocked warning */}
          {passwordBlocked && (
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">Contraseña bloqueada</p>
                <p className="text-amber-700">Has excedido los intentos. Usa código de acceso.</p>
              </div>
            </div>
          )}

          {/* Login Mode Toggle */}
          {!passwordBlocked && (
            <div className="flex gap-2 p-1 bg-slate-100 rounded-xl mb-6">
              <button
                type="button"
                onClick={() => setLoginMode('password')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  loginMode === 'password' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <Lock className="w-4 h-4" />
                Contraseña
              </button>
              <button
                type="button"
                onClick={() => setLoginMode('magic')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  loginMode === 'magic' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <KeyRound className="w-4 h-4" />
                Código
              </button>
            </div>
          )}

          {(loginMode === 'password' && !passwordBlocked) ? (
            /* Password Login Form */
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Correo electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': primaryColor }}
                    placeholder="correo@ejemplo.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full pl-12 pr-12 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': primaryColor }}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {passwordAttempts > 0 && passwordAttempts < 3 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {3 - passwordAttempts} intentos restantes antes de bloqueo
                  </p>
                )}
              </div>

              {/* reCAPTCHA for password login */}
              {isRecaptchaEnabled && (
                <div className="flex justify-center" data-testid="recaptcha-container">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={settings.recaptchaSiteKey}
                    onChange={handleRecaptchaChange}
                    onExpired={handleRecaptchaExpired}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading || (isRecaptchaEnabled && !recaptchaToken)}
                className="w-full py-3.5 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
                style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    Ingresar
                  </>
                )}
              </button>
            </form>
          ) : (
            /* Magic Code Form */
            <form onSubmit={handleRequestCode} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Correo electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': primaryColor }}
                    placeholder="correo@ejemplo.com"
                    required
                  />
                </div>
              </div>

              <p className="text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
                Te enviaremos un código de 6 dígitos a tu correo. El código expira en 4 minutos.
              </p>

              {/* reCAPTCHA for magic code */}
              {isRecaptchaEnabled && (
                <div className="flex justify-center" data-testid="recaptcha-container-magic">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={settings.recaptchaSiteKey}
                    onChange={handleRecaptchaChange}
                    onExpired={handleRecaptchaExpired}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading || (isRecaptchaEnabled && !recaptchaToken)}
                className="w-full py-3.5 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
                style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}
                data-testid="magic-code-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    Enviar código
                  </>
                )}
              </button>
            </form>
          )}

          <div className="flex items-center justify-center gap-2 text-xs text-slate-400 mt-4">
            <Shield className="w-3.5 h-3.5" />
            <span>Acceso protegido</span>
          </div>
        </div>

        <p className="text-center text-white/60 text-sm mt-6">
          © 2024 {settings?.appName || 'DataCRM'}. Todos los derechos reservados.
        </p>
        <p className="text-center text-white/40 text-xs mt-2">
          Desarrollado por <a href="https://wearedatalab.com" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white/80 underline transition-colors">wearedatalab.com</a>
        </p>
      </div>
    </div>
  );
}
